import React, { useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import { MultiSelectChips } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal from 'components/shared/StandardModal';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { Client } from 'models/flexible/Client';
import { Collaboration } from 'models/flexible/Collaboration';
import { adminClientsQueryKeys, adminIndexClients } from 'services/api/admin/clients';
import { adminCollaborationsQueryKeys, adminUpdateCollaboration } from 'services/api/admin/collaborations';
import { useToastActions } from 'stores/toastStore';

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;

type Props = {
  client: Client | Collaboration;
  onCancel: () => void;
};

export default function CollaborationModal({ client, onCancel }: Props) {
  const collaboration = useMemo(() => new Collaboration(client), [client]);
  const [collaborators, setCollaborators] = useState<Client[]>(collaboration.collaborators as unknown as Client[]);
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const clientOptions = useInfiniteOptions({
    queryKey: ['adminClients', 'collaboration', client.id],
    fetchFn: adminIndexClients,
    params: { 'id.not': client.id, sortBy: 'name asc' },
  });

  const mutation = useMutation({
    mutationFn: () => adminUpdateCollaboration(collaboration.id, { collaboratorIds: collaborators.map((c) => c.id) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCollaborationsQueryKeys.index() });
      queryClient.invalidateQueries({ queryKey: adminClientsQueryKeys.index() });
      addToast({ text: 'Collaborations successfully updated' });
      onCancel();
    },
    onError: () => {
      addToast({ text: 'There was an error updating the collaborations' });
    },
  });

  return (
    <StandardModal title='Client Collaborations' onCancel={onCancel}>
      <StandardModal.ModalContent>
        <p style={{ margin: '0 0 16px', fontWeight: 600 }}>Primary Owner: {collaboration.name}</p>
        <div style={{ textAlign: 'left' }}>
          <InputGroup>
            <MultiSelectChips
              title='Collaborating Clients'
              {...clientOptions}
              values={collaborators}
              onChange={setCollaborators}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select clients...'
              menuPortalTarget={document.body}
            />
          </InputGroup>
        </div>
      </StandardModal.ModalContent>
      <StandardModal.Actions>
        <ButtonGroup>
          <Button variant='ghost' onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            Save Collaborations
          </Button>
        </ButtonGroup>
      </StandardModal.Actions>
    </StandardModal>
  );
}
